// Ads with these Google RSA ad strengths are always audited. It is not a user setting.
export const MANDATORY_AD_STRENGTH_NOTE =
  'Ads with Poor or Average ad strength are always included. This cannot be turned off.';

export const DEFAULT_MIN_SPEND = 100;
export const DEFAULT_MAX_ADS = 10;
export const MAX_ADS_LIMIT = 1000;
export const DEFAULT_MIN_ACTIVE_DAYS = 30;
export const DEFAULT_LOOKBACK_DAYS = 30;
export const DEFAULT_MIN_CONVERSIONS = 1;
export const MAX_CONFIG_NAME_LENGTH = 60;

// How often a config's scheduled audit runs.
export const ScheduleFrequency = {
  WEEKLY: 'weekly',
  BIWEEKLY: 'biweekly',
  MONTHLY: 'monthly',
  EVERY_N_DAYS: 'everyNDays'
} as const;
export type ScheduleFrequencyType = (typeof ScheduleFrequency)[keyof typeof ScheduleFrequency];

export const SCHEDULE_FREQUENCY_LABELS: Record<ScheduleFrequencyType, string> = {
  weekly: 'Weekly',
  biweekly: 'Every 2 weeks',
  monthly: 'Monthly',
  everyNDays: 'Every N days (daily)'
};

// 0 = Sunday .. 6 = Saturday, matching the lambda's `daysOfWeek`.
export const DAY_OF_WEEK_SHORT_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
// Once a week, on Monday.
export const DEFAULT_DAYS_OF_WEEK = [1];
export const DEFAULT_DAY_OF_MONTH = 1;
export const DEFAULT_SCHEDULE_TIME = '07:00';
export const DEFAULT_SCHEDULE_TIMEZONE = 'America/Los_Angeles';

// Short list of common zones; a saved schedule in any other IANA zone is still shown as-is.
export const TIMEZONE_OPTIONS: { label: string; shortLabel: string; value: string }[] = [
  { label: 'Pacific (Los Angeles)', shortLabel: 'PT', value: 'America/Los_Angeles' },
  { label: 'Mountain (Denver)', shortLabel: 'MT', value: 'America/Denver' },
  { label: 'Arizona (Phoenix)', shortLabel: 'AZ', value: 'America/Phoenix' },
  { label: 'Central (Chicago)', shortLabel: 'CT', value: 'America/Chicago' },
  { label: 'Eastern (New York)', shortLabel: 'ET', value: 'America/New_York' },
  { label: 'Alaska (Anchorage)', shortLabel: 'AKT', value: 'America/Anchorage' },
  { label: 'Hawaii (Honolulu)', shortLabel: 'HT', value: 'Pacific/Honolulu' },
  { label: 'UTC', shortLabel: 'UTC', value: 'UTC' }
];

// Every 30 minutes: '00:00', '00:30', ... '23:30'.
export const SCHEDULE_TIME_OPTIONS: string[] = Array.from({ length: 48 }, (_, i) => {
  const hour = String(Math.floor(i / 2)).padStart(2, '0');
  return `${hour}:${i % 2 === 0 ? '00' : '30'}`;
});

// Wire shape of a schedule (mirrors the lambda's AuditConfigSchedule).
export interface AuditSchedule {
  anchorDate?: string; // YYYY-MM-DD, biweekly and everyNDays
  dayOfMonth?: number; // monthly only, 1-31 (a shorter month runs on its last day)
  daysOfWeek?: number[]; // weekly and biweekly
  frequency: ScheduleFrequencyType;
  intervalDays?: number; // everyNDays only
  time: string; // HH:00 or HH:30
  timezone: string; // IANA zone
}

// Form state for a schedule: numeric/date inputs stay strings while editing.
export interface AuditScheduleDraft {
  anchorDate: string;
  dayOfMonth: string;
  daysOfWeek: number[];
  frequency: ScheduleFrequencyType;
  intervalDays: string;
  time: string;
  timezone: string;
}

// How a config generates copy. Admin-only; mirrors bpm-types AuditModelConfig.
export interface AuditModelConfig {
  additionalInstructions?: string;
  instanceUuid?: string;
  styleGuideUuid?: string;
  temperature?: number;
  topK?: number;
  topP?: number;
}

// A saved audit config. An ad is flagged for regeneration when it is Poor/Average strength (always),
// clears the spend and active-days thresholds (spend/conversions are measured over the last
// `lookbackDays`), AND it underperforms on EITHER conversion metric
// (conversions <= minConversions OR conversion value <= minConversionValue).
// A null metric is skipped, but at least one of the two must be set.
export interface AuditConfig {
  configID: string;
  createdBy?: string;
  enabled: boolean;
  externalReviewers: string[];
  internalReviewers: string[];
  lastModified: string;
  lastRunAt?: string | null;
  lastUser?: string;
  lookbackDays: number;
  maxAds: number;
  minActiveDays: number;
  minConversions: number | null;
  minConversionValue: number | null;
  minSpend: number;
  modelConfig: AuditModelConfig | null;
  name: string;
  schedule: AuditSchedule;
}

// What the UI sends on save. `configID` is omitted when creating. `schedule` and the reviewer lists
// are optional: when omitted they are left out of the request so the backend keeps stored values.
export type AuditConfigInput = Omit<
  AuditConfig,
  | 'configID'
  | 'createdBy'
  | 'externalReviewers'
  | 'internalReviewers'
  | 'lastModified'
  | 'lastRunAt'
  | 'lastUser'
  | 'modelConfig'
  | 'schedule'
> & {
  configID?: string;
  externalReviewers?: string[];
  internalReviewers?: string[];
  modelConfig?: AuditModelConfig;
  schedule?: AuditSchedule;
};

// Form state: numeric inputs are strings so an empty field can mean "rule not used".
// The *Edited flags track whether the user touched the schedule / reviewers, so an edit of an
// existing config only sends them when changed (and never overwrites with stale values).
export interface AuditConfigDraft {
  additionalInstructions: string;
  configID?: string;
  creativity: string;
  enabled: boolean;
  externalReviewers: string[];
  instanceUuid: string;
  internalReviewers: string[];
  lookbackDays: string;
  maxAds: string;
  minActiveDays: string;
  minConversions: string;
  minConversionValue: string;
  minSpend: string;
  modelEdited: boolean;
  name: string;
  reviewersEdited: boolean;
  schedule: AuditScheduleDraft;
  scheduleEdited: boolean;
  styleGuideUuid: string;
}

export type AuditConfigRules = Partial<
  Pick<
    AuditConfig,
    | 'lookbackDays'
    | 'maxAds'
    | 'minActiveDays'
    | 'minConversions'
    | 'minConversionValue'
    | 'minSpend'
  >
>;

// Wire shape of /getCopyTigerAuditConfigs rows (mirrors bpm-types AuditConfigRow, defined locally
// because the installed bpm-types may predate it). The flag checks travel in `rules`; the lambda
// stores it as opaque jsonb so the checks can evolve server-side.
export interface AuditConfigRowWire {
  company: string;
  configID: string;
  createdBy?: string;
  enabled: boolean;
  externalReviewers?: string[];
  internalReviewers?: string[];
  lastModified: string;
  lastRunAt?: string | null;
  lastUser?: string;
  modelConfig?: AuditModelConfig | null;
  name: string;
  rules: AuditConfigRules;
  schedule?: AuditSchedule;
}

// POST body of /saveCopyTigerAuditConfig (minus `company`, added by the fetch helper).
export interface AuditConfigWireBody {
  configID?: string;
  enabled: boolean;
  // The backend accepts a comma-separated string for the client list.
  externalReviewers?: string;
  internalReviewers?: string[];
  modelConfig?: AuditModelConfig;
  name: string;
  rules: AuditConfigRules;
  schedule?: AuditSchedule;
}
